import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useWallet } from "../context/web3.context";
import ProgressBar from "./ProgressBar";
import { ethers } from "ethers";

export default function ClaimLOL(): JSX.Element {
  const { lolVestingContract, address } = useWallet();
  const [loading, setLoading] = useState<boolean>(false);
  const [isAvailableLoading, setIsAvailableLoading] = useState<boolean>(false);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [available, setAvailable] = useState<number>(0);
  const [released, setReleased] = useState<number>(0);
  const [claimed, setClaimed] = useState<number>(0);
  const [allocation, setAllocation] = useState<number>(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      if (!lolVestingContract) {
        throw new Error("LOL Vesting contract not loaded");
      }
      setLoading(true);
      const tx = await lolVestingContract.claimLOL();
      await tx.wait();
      toast.success(`Transaction sent: ${tx.hash}`);
    } catch (error: any) {
      if (error.reason) {
        toast.error(error.reason.replace("execution reverted: ", ""));
      } else toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailable = useCallback(async () => {
    try {
      if (!lolVestingContract) {
        throw new Error("LOL Vesting contract not loaded");
      }
      if (!address) {
        throw new Error("Wallet address not found, please connect wallet");
      }
      const available = await lolVestingContract.available(address);
      setAvailable(parseInt(ethers.utils.formatEther(available)));
    } catch (error: any) {
      if (error.reason) {
        toast.error(error.reason.replace("execution reverted: ", ""));
      } else console.log(error.message);
    }
  }, [lolVestingContract, address]);

  const fetchClaimed = useCallback(async () => {
    try {
      if (!lolVestingContract) {
        throw new Error("LOL Vesting contract not loaded");
      }
      if (!address) {
        throw new Error("Wallet address not found, please connect wallet");
      }
      const claimed = await lolVestingContract.claimed(address);
      setClaimed(parseInt(ethers.utils.formatEther(claimed)));
    } catch (error: any) {
      if (error.reason) {
        toast.error(error.reason.replace("execution reverted: ", ""));
      } else console.log(error.message);
    }
  }, [lolVestingContract, address]);

  const calculateProgress = useCallback(async () => {
    try {
      if (!lolVestingContract) {
        throw new Error("LOL Vesting contract not loaded");
      }
      if (!address) {
        throw new Error("Wallet address not found, please connect wallet");
      }
      const allocation = await lolVestingContract.allocation(address);
      const released = await lolVestingContract.released(address);
      setAllocation(parseInt(ethers.utils.formatEther(allocation)));
      setReleased(parseInt(ethers.utils.formatEther(released)));
    } catch (error: any) {
      if (error.reason) {
        toast.error(error.reason.replace("execution reverted: ", ""));
      } else console.log(error.message);
    }
  }, [lolVestingContract, address]);

  useEffect(() => {
    setIsAvailableLoading(true);
    fetchAvailable();
    setIsAvailableLoading(false);
  }, [fetchAvailable]);

  useEffect(() => {
    setIsCalculating(true);
    calculateProgress();
    setIsCalculating(false);
  }, [calculateProgress]);

  useEffect(() => {
    fetchClaimed();
  }, [fetchClaimed]);

  return (
    <form
      id="claim"
      autoComplete="off"
      autoCorrect="off"
      className="max-w-screen-md"
      onSubmit={handleSubmit}
    >
      <div className="mb-3 flex items-start justify-center">
        {!isCalculating ? (
          <ProgressBar
            text={`Allocation (${released}/${allocation})`}
            progress_1={Math.floor((claimed / released) * 100) || 0}
            progress_2={Math.floor((released / allocation) * 100) || 0}
            outerClassName="w-full"
          />
        ) : (
          <div className="flex items-center">
            <div role="status">
              <svg
                aria-hidden="true"
                className="w-4 h-4 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span className="sr-only">Loading...</span>
            </div>
            <div>Loading Stats...</div>
          </div>
        )}
      </div>
      <div className="mb-6 flex items-start justify-center">
        {!isAvailableLoading ? (
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Available LOL to claim:{" "}
            <span className="font-bold text-gray-900 dark:text-gray-100">
              {available} LOL
            </span>
          </p>
        ) : (
          <div className="flex items-center">
            <div role="status">
              <svg
                aria-hidden="true"
                className="w-4 h-4 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span className="sr-only">Loading...</span>
            </div>
            <div>Loading available...</div>
          </div>
        )}
      </div>
      <div className="flex items-start mb-6">
        <div className="flex items-center h-5">
          <input id="terms" type="checkbox" value="" className="" required />
        </div>
        <label
          htmlFor="terms"
          className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
        >
          I agree with the{" "}
          <a
            href="/terms"
            className="text-blue-600 hover:underline dark:text-blue-500"
          >
            terms and conditions
          </a>
        </label>
      </div>
      <button
        type="submit"
        className={`text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 ${
          loading || isAvailableLoading || available < 1
            ? "opacity-50 cursor-not-allowed"
            : ""
        }`}
      >
        {loading && isAvailableLoading ? (
          <div className="flex items-center">
            <div role="status">
              <svg
                aria-hidden="true"
                className="w-4 h-4 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span className="sr-only">Loading...</span>
            </div>
            <div>Loading...</div>
          </div>
        ) : available && available > 1 ? (
          "Claim"
        ) : (
          "No LOL to claim"
        )}
      </button>
    </form>
  );
}
